/**
 * Dark-mode scrollbar aesthetics — "scroll-to-see" activity tracking (Dark mode
 * aesthtic #192).
 *
 * Webkit scrollbars expose no ":scrolling" pseudo-class, so we emulate one. A single
 * capture-phase `scroll` listener on `document` catches scroll events from EVERY
 * overflow container in the app (scroll does not bubble, but it IS delivered during
 * the capture phase), tags the actively-scrolled element with the `omnis-scrolling`
 * class, and removes it after a short idle window once scrolling stops.
 *
 * `index.css` reveals a subtle, semi-transparent scrollbar thumb only while that
 * class is present — and ONLY in dark mode (`html.dark`). In light mode the class is
 * still toggled but the light-mode scrollbar CSS ignores it, so behaviour there is
 * unchanged (per the ticket: White Mode stays as-is).
 *
 * One global listener (installed once) keeps this cheap and consistent across every
 * scrollable frame — sidebar, chat, Telegram threads, Kanban boards,
 * calendar, expenses — without every component wiring its own handler.
 */

const IDLE_MS = 700;
const ACTIVE_CLASS = 'omnis-scrolling';

let installed = false;
const timers = new WeakMap();

function scrollTargetElement(target) {
    // Root/window scroll delivers `document` (or `window`) as the event target; the
    // scrollbar for that scroll lives on the <html> element.
    if (target === document || target === window) return document.documentElement;
    if (target && target.nodeType === 1) return target;
    return null;
}

function handleScroll(event) {
    const el = scrollTargetElement(event.target);
    if (!el) return;

    el.classList.add(ACTIVE_CLASS);

    const existing = timers.get(el);
    if (existing) clearTimeout(existing);

    timers.set(
        el,
        setTimeout(() => {
            el.classList.remove(ACTIVE_CLASS);
            timers.delete(el);
        }, IDLE_MS)
    );
}

export function installScrollActivityTracking() {
    if (installed || typeof document === 'undefined') return;
    installed = true;
    // Capture phase so we observe scrolls on any nested overflow container; passive so
    // we never block scrolling.
    document.addEventListener('scroll', handleScroll, { capture: true, passive: true });
}

// Self-install on import (idempotent).
installScrollActivityTracking();
